import { Container, Box, Typography, Link, useTheme } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import { LanguageButton } from "../components/languageButton/LanguageButton";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { common } from "../i18n";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/UserContext";

import {
  AUTOPLAY_INTERVAL_MS,
  SLIDE_TRANSITION_MS,
  SUPPORT_EMAIL,
  COMPANY_WEBSITE,
} from "../constants";

// กัน open-redirect: ต้องเป็น absolute path ของเราเอง ไม่ใช่ //evil.example หรือ javascript:
const isSafeInternalPath = (path: string): boolean =>
  path.startsWith("/") && !path.startsWith("//");

interface LayoutProps {
  children: ReactNode;
  // ใช้ตอนที่หน้านั้นต้องคุม redirect เอง (เช่น /auth/sso ที่ต้อง consume handoff
  // code ก่อนถึงจะพา user ไป /select-organization). ปล่อย undefined = พฤติกรรมเดิม.
  disableAutoRedirect?: boolean;
  // opt-in only: SSO pages เพิ่ม hero gradient wash + mobile trust line; /login, /auth/link ไม่ส่ง prop นี้ = ไม่เปลี่ยนแปลง
  heroCue?: boolean;
}

const LoginLayout = ({ children, disableAutoRedirect, heroCue }: LayoutProps) => {
  const theme = useTheme();
  const day = dayjs();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { t, i18n } = useTranslation([common], { nsMode: "fallback" });

  const [activeSlide, setActiveSlide] = useState(0);

  const SLIDES = [t("Login-Slide-1"), t("Login-Slide-2"), t("Login-Slide-3")];

  useEffect(() => {
    if (disableAutoRedirect) return;
    if (user != null) {
      // /auth/link bounce ผ่าน /login?returnTo=... — pages อื่นที่ใช้ LoginLayout
      // จะไม่ตั้ง returnTo เลย ดังนั้น branch นี้เป็น no-op สำหรับ flow ปกติ
      const returnTo = searchParams.get("returnTo");
      if (returnTo && isSafeInternalPath(returnTo)) {
        navigate(returnTo, { replace: true });
      } else {
        navigate("/select-organization");
      }
    }
  }, [navigate, user, disableAutoRedirect, searchParams]);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % SLIDES.length);
    }, AUTOPLAY_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [SLIDES.length]);

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        display: "flex",
        minHeight: "100vh",
        width: "100%",
      }}
    >
      {/* Left Section (Banner & Carousel) */}
      <Box
        sx={[
          {
            flex: 5,
            display: { xs: "none", md: "block" },
            backgroundImage: "url('/img/login/BGImageAuth.webp')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            p: "60px",
          },
          // heroCue ต้อง relative-position ตัว pane เอง ไม่งั้น overlay ด้านล่างจะ absolute เทียบกับ ancestor อื่น
          heroCue === true && { position: "relative" },
        ]}
      >
        {heroCue === true && (
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(to right, transparent 40%, var(--green-06) 100%)",
              pointerEvents: "none",
            }}
          />
        )}
        <Box
          sx={[
            {
              display: "flex",
              flexDirection: "column",
              justifyContent: "end",
              width: "100%",
              height: "100%",
              maxWidth: "400px",
            },
            // zIndex 1 คุมให้ carousel/contact อยู่เหนือ gradient wash เสมอ
            heroCue === true && { position: "relative", zIndex: 1 },
          ]}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "end",
              width: "100%",
              maxWidth: "260px",
              color: "common.white",
            }}
          >
            {/* Carousel Section */}
            <Box sx={{ overflow: "hidden", width: "100%" }}>
              <Box
                sx={{
                  display: "flex",
                  transition: `transform ${SLIDE_TRANSITION_MS}ms ease`,
                  transform: `translateX(-${activeSlide * 100}%)`,
                }}
              >
                {SLIDES.map((text, i) => (
                  <Box key={i} sx={{ minWidth: "100%", flexShrink: 0 }}>
                    <Typography variant="h1" sx={{ maxWidth: "260px" }}>
                      {text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Pagination Dots */}
            <Box sx={{ display: "flex", gap: "6px", mt: "25px" }}>
              {SLIDES.map((_, i) => (
                <Box
                  key={i}
                  component="button"
                  onClick={() => setActiveSlide(i)}
                  sx={{
                    width: "12px",
                    height: "12px",
                    borderRadius: "50%",
                    border: "none",
                    cursor: "pointer",
                    p: 0,
                    bgcolor:
                      i === activeSlide ? "primary.main" : "common.white",
                    transition: "background-color 0.3s ease",
                  }}
                />
              ))}
            </Box>

            {/* Powered By */}
            <Box sx={{ mt: "60px" }}>
              <Typography variant="h3">{t("Powered-By")}</Typography>
              <Box component="img" src="/img/Logo-locarb.svg" alt="logo" />
            </Box>

            {/* Contact Section */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                mt: "20px",
                gap: "8px",
              }}
            >
              <Typography variant="h3">{t("Contact-Us")}</Typography>

              <Link
                href={`mailto:${SUPPORT_EMAIL}`}
                variant="body1"
                sx={{
                  color: "inherit",
                  textDecoration: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
                target="_blank"
              >
                <EmailIcon />
                {SUPPORT_EMAIL}
              </Link>

              <Link
                href={COMPANY_WEBSITE}
                variant="body1"
                sx={{
                  color: "inherit",
                  textDecoration: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
                target="_blank"
              >
                <WebIcon />
                {COMPANY_WEBSITE}
              </Link>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Right Section (Login Form) */}
      <Box
        sx={{
          flex: 7,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          minHeight: "100vh",
          width: "100%",
          position: "relative",
        }}
      >
        {/* pinned to the pane corner so it holds the top-right at any resolution */}
        <Box sx={{ position: "absolute", top: "20px", right: "20px", zIndex: 2 }}>
          <LanguageButton />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            // fills the space above the footer and centers within it — resolution-independent, footer always reserved so it never overlaps
            flex: 1,
            width: "100%",
            // border-box so the horizontal padding stays inside 100% — otherwise it overflows the pane and knocks the card off the shared center axis
            boxSizing: "border-box",
            justifyContent: "center",
            alignItems: "center",
            p: heroCue === true ? { xs: "40px 24px 0px 24px", md: "60px 60px 0px 60px" } : "60px 60px 0px 60px",
            position: "relative",
          }}
        >
          {children}
        </Box>

        {heroCue === true && (
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              justifyContent: "center",
              alignItems: "center",
              gap: "8px",
              pb: "12px",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary }}
            >
              {t("SSO-Mobile-Trust-Line")}
            </Typography>
          </Box>
        )}

        <footer>
          <Box sx={{ textAlign: "center", pb: heroCue === true ? "32px" : "60px" }}>
            <Typography
              variant="text1"
              sx={{ color: theme.palette.text.secondary }}
            >
              {t("Copyright", {
                year: i18n.language === "th" ? day.year() + 543 : day.year(),
              })}
            </Typography>
          </Box>
        </footer>
      </Box>
    </Container>
  );
};

// แยกส่วนของ SVG
const EmailIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="25"
    viewBox="0 0 24 25"
    fill="none"
  >
    <path
      d="M17 21.2769H7C3.35 21.2769 1.25 19.1769 1.25 15.5269V8.52686C1.25 4.87686 3.35 2.77686 7 2.77686H17C20.65 2.77686 22.75 4.87686 22.75 8.52686V15.5269C22.75 19.1769 20.65 21.2769 17 21.2769ZM7 4.27686C4.14 4.27686 2.75 5.66686 2.75 8.52686V15.5269C2.75 18.3869 4.14 19.7769 7 19.7769H17C19.86 19.7769 21.25 18.3869 21.25 15.5269V8.52686C21.25 5.66686 19.86 4.27686 17 4.27686H7Z"
      fill="white"
    />
    <path
      d="M11.9998 12.8968C11.1598 12.8968 10.3098 12.6368 9.65978 12.1068L6.52978 9.60683C6.20978 9.34683 6.14978 8.87682 6.40978 8.55682C6.66978 8.23682 7.13978 8.17683 7.45978 8.43683L10.5898 10.9368C11.3498 11.5468 12.6398 11.5468 13.3998 10.9368L16.5298 8.43683C16.8498 8.17683 17.3298 8.22682 17.5798 8.55682C17.8398 8.87682 17.7898 9.35683 17.4598 9.60683L14.3298 12.1068C13.6898 12.6368 12.8398 12.8968 11.9998 12.8968Z"
      fill="white"
    />
  </svg>
);

const WebIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="25"
    viewBox="0 0 24 25"
    fill="none"
  >
    <path
      d="M12 22.7769C6.07 22.7769 1.25 17.9569 1.25 12.0269C1.25 6.09686 6.07 1.27686 12 1.27686C17.93 1.27686 22.75 6.09686 22.75 12.0269C22.75 17.9569 17.93 22.7769 12 22.7769ZM12 2.77686C6.9 2.77686 2.75 6.92686 2.75 12.0269C2.75 17.1269 6.9 21.2769 12 21.2769C17.1 21.2769 21.25 17.1269 21.25 12.0269C21.25 6.92686 17.1 2.77686 12 2.77686Z"
      fill="white"
    />
    <path
      d="M9.00023 21.7769H8.00023C7.59023 21.7769 7.25023 21.4369 7.25023 21.0269C7.25023 20.6169 7.57023 20.2869 7.98023 20.2769C6.41023 14.9169 6.41023 9.13686 7.98023 3.77686C7.57023 3.76686 7.25023 3.43686 7.25023 3.02686C7.25023 2.61686 7.59023 2.27686 8.00023 2.27686H9.00023C9.24023 2.27686 9.47023 2.39686 9.61023 2.58686C9.75023 2.78686 9.79023 3.03686 9.71023 3.26686C7.83023 8.91686 7.83023 15.1369 9.71023 20.7969C9.79023 21.0269 9.75023 21.2769 9.61023 21.4769C9.47023 21.6569 9.24023 21.7769 9.00023 21.7769Z"
      fill="white"
    />
    <path
      d="M15.0004 21.777C14.9204 21.777 14.8404 21.767 14.7604 21.737C14.3704 21.607 14.1504 21.177 14.2904 20.787C16.1704 15.137 16.1704 8.91704 14.2904 3.25704C14.1604 2.86704 14.3704 2.43704 14.7604 2.30704C15.1604 2.17704 15.5804 2.38704 15.7104 2.77704C17.7004 8.73704 17.7004 15.297 15.7104 21.247C15.6104 21.577 15.3104 21.777 15.0004 21.777Z"
      fill="white"
    />
    <path
      d="M12 17.2267C9.21 17.2267 6.43 16.8367 3.75 16.0467C3.74 16.4467 3.41 16.7767 3 16.7767C2.59 16.7767 2.25 16.4367 2.25 16.0267V15.0267C2.25 14.7867 2.37 14.5567 2.56 14.4167C2.76 14.2767 3.01 14.2367 3.24 14.3167C8.89 16.1967 15.12 16.1967 20.77 14.3167C21 14.2367 21.25 14.2767 21.45 14.4167C21.65 14.5567 21.76 14.7867 21.76 15.0267V16.0267C21.76 16.4367 21.42 16.7767 21.01 16.7767C20.6 16.7767 20.27 16.4567 20.26 16.0467C17.57 16.8367 14.79 17.2267 12 17.2267Z"
      fill="white"
    />
    <path
      d="M20.9998 9.77672C20.9198 9.77672 20.8398 9.76672 20.7598 9.73672C15.1098 7.85672 8.87978 7.85672 3.22978 9.73672C2.82978 9.86672 2.40978 9.65672 2.27978 9.26672C2.15978 8.86672 2.36978 8.44672 2.75978 8.31672C8.71978 6.32672 15.2798 6.32672 21.2298 8.31672C21.6198 8.44672 21.8398 8.87672 21.6998 9.26672C21.6098 9.57672 21.3098 9.77672 20.9998 9.77672Z"
      fill="white"
    />
  </svg>
);

export default LoginLayout;
